import {LOCATION_CHANGE} from 'connected-react-router'
import {fork, put, take, takeLatest} from 'redux-saga/effects'

import {
  GET_PARTICIPANT_INFO_FAILURE,
  GET_PARTICIPANT_INFO_SUCCESS,
  getParticipantInfo,
  getSoonestEventDate,
} from '../actions/api'
import {closeDashboard, openDashboard, setSectionsOpen} from '../actions/dashboard'
import {activatePodcastHelloBar} from '../actions/helloBar'
import {podcastCampaignSlugs} from '../actions/helloBar/enums'
import {closeMobileMenu} from '../actions/mobileMenu'
import {getNearestShowrooms} from '../actions/nearestShowrooms'
import {
  INITIAL_PAGE_LOAD,
  SUBSEQUENT_PAGE_LOAD,
  canonicalize,
  decanonicalize,
  initialPageLoad,
  initialPageLoadExistingSession,
  initialPageLoadNewSession,
  subsequentPageLoad,
  windowOnload,
} from '../actions/site'
import * as facebook from '../analytics/facebook'
import * as optimizely from '../analytics/optimizely'
import * as segment from '../analytics/segment'
import * as api from '../api/api'
import * as auth from '../auth'
import {openOnboarding, setStep} from '../components/Onboarding/redux/actions'
import eventChannel from '../eventChannel'
import {OnboardingSteps} from '../store/enum'
import * as user from '../store/user'
import * as track from '../track/track'
import {getUrlParam, hasUrlParam, isFilledString} from '../utils'
import {forUserAndEvent} from './login'

let pageChangedWorkerFiredOnce = false

// -----------------
// Every Page Change
// -----------------

function* pageChangedWorker() {
  // Initial or Subsequent Page Loads
  // --------------------------------

  yield put(getNearestShowrooms())
  if (!pageChangedWorkerFiredOnce) {
    // check for inf token, i.e. Login as User
    // -- NOTE: This will block the UI until the API call resolves! --
    const inf = getUrlParam('inf')
    if (isFilledString(inf)) yield api.getJwtForLoginAsUser(inf)

    // check for participant and token params, i.e. Participant Login & Registration
    const participant = hasUrlParam('participant')
    const token = getUrlParam('token')
    if (participant && isFilledString(token)) {
      yield put(getParticipantInfo(token))

      // wait for the result of `getParticipantInfo`
      yield take([GET_PARTICIPANT_INFO_FAILURE, GET_PARTICIPANT_INFO_SUCCESS])
    }

    // initialize analytics, before other things start to call them
    segment.pageLoaded()
    facebook.pageLoaded()
    optimizely.pageLoaded()

    yield put(initialPageLoad())
  } else {
    yield put(subsequentPageLoad())
  }

  // Every Time
  // --------------------------------

  // segment page view tracking
  track.pageView()

  // deindex or reindex the current page
  if (getUrlParam('hello')) {
    // canonicalize page so hello bar urls aren't indexed
    yield put(canonicalize())
  } else {
    // decanonicalize page (maybe not necessary, but it keeps things tidy)
    yield put(decanonicalize())
  }

  // Lastly...
  // ---------

  // make a note that this worker has been called once
  pageChangedWorkerFiredOnce = true

  // for legacy code...
  eventChannel.trigger(eventChannel.pathnameChanged)
}

export function* pageChangedSaga() {
  yield takeLatest(LOCATION_CHANGE, pageChangedWorker)
}

// -----------------
// Initial Page Load
// -----------------

function* dashboardParams() {
  const hasDetailsParam = hasUrlParam('details')
  const hasDashboadParam = hasUrlParam('dashboard')
  // NOTE: Saga will be delayed here.
  const state = yield forUserAndEvent()

  if (hasDetailsParam) {
    yield put(setSectionsOpen({eventDetailsExpanded: hasDetailsParam}))
  }

  if (hasDashboadParam) {
    if (user.isAuthenticated(state)) {
      yield put(openDashboard())
    } else {
      yield put(setStep(OnboardingSteps.login))
      yield put(openOnboarding())
    }
  }
}

// NOTE: The pwreset param will not be needed after SHOP-2808 is released.
// when an un-authed user resets their password using the form from
//  the email link, the backend will redirect them to the homepage
//  with onboarding open and their email address in the email input
function* pwresetParam() {
  const hasPwresetParam = hasUrlParam('pwreset')
  // NOTE: Saga will be delayed here.
  const state = yield forUserAndEvent()

  if (!user.isAuthenticated(state) && hasPwresetParam) {
    yield put(setStep(OnboardingSteps.login))
    yield put(openOnboarding())
  }
}

// Worker to detect when the window onload event has fired. This is used to signal to components
// that it's a good time to start preloading of lazy-loaded assets.
function* windowOnloadWorker() {
  yield new Promise((resolve) => {
    const listener = () => {
      if (document.readyState === 'complete') {
        window.removeEventListener('load', listener)
        resolve()
      }
    }

    window.addEventListener('load', listener)
    listener()
  })

  yield put(windowOnload())
}

function* podcastHelloBarParam() {
  const hasUtmCampaign = hasUrlParam('utm_campaign')
  const utmContentParam = getUrlParam('utm_campaign')

  if (hasUtmCampaign && !!podcastCampaignSlugs[utmContentParam]) {
    yield put(activatePodcastHelloBar(podcastCampaignSlugs[utmContentParam]))
  }
}

function* initialPageLoadWorker() {
  // handle url params; we don't need or want to wait for each one to
  //  finish before proceeding to the next
  yield fork(dashboardParams)
  yield fork(pwresetParam)

  yield fork(windowOnloadWorker)
  yield fork(podcastHelloBarParam)

  // expired user session
  if (getUrlParam('expired')) track.sessionExpired()

  // API calls that don't require authentication
  yield put(getSoonestEventDate())
}

export function* initialPageLoadSaga() {
  yield takeLatest(INITIAL_PAGE_LOAD, initialPageLoadWorker)
}

// --------------------
// Subsequent Page Load
// --------------------

function* subsequentPageLoadWorker() {
  // close site components
  yield put(closeDashboard())
  yield put(closeMobileMenu())
  // see `closeModalsWorker` for more things closing on SUBSEQUENT_PAGE_LOAD
}

export function* subsequentPageLoadSaga() {
  yield takeLatest(SUBSEQUENT_PAGE_LOAD, subsequentPageLoadWorker)
}

/**
 * Worker saga which dispatches actions to determine if we're in an existing session or new session
 * using the JWT token stored in local storage.
 */
function* checkSessionWorker() {
  if (auth.hasJwt()) {
    yield put(initialPageLoadExistingSession())
  } else {
    yield put(initialPageLoadNewSession())
  }
}

/**
 * Root saga which listens for intial page load event to handle checking of the current JWT session token.
 */
export function* checkSessionSaga() {
  yield takeLatest(INITIAL_PAGE_LOAD, checkSessionWorker)
}
